/**
 * Organization service for Hugo_Organizations Firestore collection.
 *
 * Document structure:
 * - name: string
 * - industry: string
 * - sites: array [{ id, name, location }]
 * - departments: array [{ id, name, siteId }]
 * - teams: array [{ id, name, departmentId }]
 * - createdAt: Timestamp
 * - createdBy: string (uid)
 * - admins: array [uid strings]
 * - thresholds: map { reliability, efficiency, productivity, safety } (defaults: 6)
 */
import { db } from '../firebase';
import { guardWrite } from './actingContext';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';

import { logActivity } from '../utils/firestore';
import { invalidateLibraryPrefCache } from './documentService';

const COLLECTION_NAME = 'Hugo_Organizations';

const DEFAULT_THRESHOLDS = {
  reliability: 6,
  efficiency: 6,
  productivity: 6,
  safety: 6,
};

/**
 * Generate a unique ID for sub-items (sites, departments, teams)
 */
function generateId() {
  return Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
}

/**
 * Create a new organization
 */
export async function createOrganization({ name, industry, createdBy }) {
  const orgData = {
    name,
    industry: industry || '',
    sites: [],
    departments: [],
    teams: [],
    createdAt: Timestamp.now(),
    createdBy,
    admins: [createdBy],
    thresholds: { ...DEFAULT_THRESHOLDS },
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), orgData);

  // Log organization creation activity
  logActivity(createdBy, 'organization_created', '', {
    organizationId: docRef.id,
    name,
  });

  return { id: docRef.id, ...orgData };
}

/**
 * Get an organization by ID
 */
export async function getOrganization(orgId) {
  const docRef = doc(db, COLLECTION_NAME, orgId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) {
    return null;
  }
  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Get all organizations for a user (where user is admin or member)
 */
export async function getOrganizationsForUser(userId) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('admins', 'array-contains', userId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get all organizations (superadmin)
 */
export async function getAllOrganizations() {
  const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to real-time updates for an organization
 */
export function subscribeToOrganization(orgId, callback) {
  const docRef = doc(db, COLLECTION_NAME, orgId);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
    } else {
      callback(null);
    }
  });
}

/**
 * Subscribe to organizations for a user
 */
export function subscribeToUserOrganizations(userId, callback) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('admins', 'array-contains', userId)
  );
  return onSnapshot(q, (snapshot) => {
    const orgs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(orgs);
  });
}

/**
 * Update organization name/industry
 */
export async function updateOrganization(orgId, updates) {
  guardWrite('organization_update', { summary: `updated organization settings (${Object.keys(updates || {}).join(', ')})`, organizationId: orgId });
  const docRef = doc(db, COLLECTION_NAME, orgId);
  await updateDoc(docRef, updates);
}

/**
 * Delete an organization
 */
export async function deleteOrganization(orgId) {
  const docRef = doc(db, COLLECTION_NAME, orgId);
  await deleteDoc(docRef);
}

// --- Sites ---

/**
 * Add a site to an organization
 */
export async function addSite(orgId, { name, location, code }) {
  guardWrite('site_add', { summary: `added site ${name}`, organizationId: orgId });
  const org = await getOrganization(orgId);
  if (!org) throw new Error('Organization not found');

  // `code` = 2–4 letter site code used in OE Condition IDs (P2-06 / P4-19)
  const newSite = { id: generateId(), name, location: location || '', ...(code ? { code: String(code).toUpperCase() } : {}) };
  const updatedSites = [...(org.sites || []), newSite];
  await updateDoc(doc(db, COLLECTION_NAME, orgId), { sites: updatedSites });
  return newSite;
}

/**
 * Update a site
 */
export async function updateSite(orgId, siteId, updates) {
  guardWrite('site_update', { summary: 'edited a site', organizationId: orgId });
  const org = await getOrganization(orgId);
  if (!org) throw new Error('Organization not found');

  const updatedSites = (org.sites || []).map((s) =>
    s.id === siteId ? { ...s, ...updates } : s
  );
  await updateDoc(doc(db, COLLECTION_NAME, orgId), { sites: updatedSites });
}

/**
 * Delete a site (and cascade delete its departments and teams)
 */
export async function deleteSite(orgId, siteId) {
  guardWrite('site_delete', { summary: 'removed a site', organizationId: orgId });
  const org = await getOrganization(orgId);
  if (!org) throw new Error('Organization not found');

  const updatedSites = (org.sites || []).filter((s) => s.id !== siteId);
  // Cascade: remove departments belonging to this site
  const deptIdsToRemove = (org.departments || [])
    .filter((d) => d.siteId === siteId)
    .map((d) => d.id);
  const updatedDepartments = (org.departments || []).filter((d) => d.siteId !== siteId);
  // Cascade: remove teams belonging to removed departments
  const updatedTeams = (org.teams || []).filter(
    (t) => !deptIdsToRemove.includes(t.departmentId)
  );

  await updateDoc(doc(db, COLLECTION_NAME, orgId), {
    sites: updatedSites,
    departments: updatedDepartments,
    teams: updatedTeams,
  });
}

// --- Departments ---

/**
 * Add a department to a site
 */
export async function addDepartment(orgId, { name, siteId }) {
  guardWrite('department_add', { summary: `added department ${name}`, organizationId: orgId });
  const org = await getOrganization(orgId);
  if (!org) throw new Error('Organization not found');

  const newDept = { id: generateId(), name, siteId };
  const updatedDepts = [...(org.departments || []), newDept];
  await updateDoc(doc(db, COLLECTION_NAME, orgId), { departments: updatedDepts });
  return newDept;
}

/**
 * Update a department
 */
export async function updateDepartment(orgId, deptId, updates) {
  guardWrite('department_update', { summary: 'edited a department', organizationId: orgId });
  const org = await getOrganization(orgId);
  if (!org) throw new Error('Organization not found');

  const updatedDepts = (org.departments || []).map((d) =>
    d.id === deptId ? { ...d, ...updates } : d
  );
  await updateDoc(doc(db, COLLECTION_NAME, orgId), { departments: updatedDepts });
}

/**
 * Delete a department (and cascade delete its teams)
 */
export async function deleteDepartment(orgId, deptId) {
  guardWrite('department_delete', { summary: 'removed a department', organizationId: orgId });
  const org = await getOrganization(orgId);
  if (!org) throw new Error('Organization not found');

  const updatedDepts = (org.departments || []).filter((d) => d.id !== deptId);
  const updatedTeams = (org.teams || []).filter((t) => t.departmentId !== deptId);

  await updateDoc(doc(db, COLLECTION_NAME, orgId), {
    departments: updatedDepts,
    teams: updatedTeams,
  });
}

// --- Teams ---

/**
 * Add a team to a department
 */
export async function addTeam(orgId, { name, departmentId, nwtType }) {
  guardWrite('nwt_add', { summary: `added NWT ${name}`, organizationId: orgId });
  const org = await getOrganization(orgId);
  if (!org) throw new Error('Organization not found');

  // nwtType 'crew' | 'team' (P2-38): crews weigh equally in their department's rollup.
  const newTeam = { id: generateId(), name, departmentId, nwtType: nwtType === 'team' ? 'team' : 'crew' };
  const updatedTeams = [...(org.teams || []), newTeam];
  await updateDoc(doc(db, COLLECTION_NAME, orgId), { teams: updatedTeams });
  return newTeam;
}

/**
 * Update a team
 */
export async function updateTeam(orgId, teamId, updates) {
  guardWrite('nwt_update', { summary: 'edited an NWT', organizationId: orgId });
  const org = await getOrganization(orgId);
  if (!org) throw new Error('Organization not found');

  const updatedTeams = (org.teams || []).map((t) =>
    t.id === teamId ? { ...t, ...updates } : t
  );
  await updateDoc(doc(db, COLLECTION_NAME, orgId), { teams: updatedTeams });
}

/**
 * Delete a team
 */
export async function deleteTeam(orgId, teamId) {
  guardWrite('nwt_delete', { summary: 'removed an NWT', organizationId: orgId });
  const org = await getOrganization(orgId);
  if (!org) throw new Error('Organization not found');

  const updatedTeams = (org.teams || []).filter((t) => t.id !== teamId);
  await updateDoc(doc(db, COLLECTION_NAME, orgId), { teams: updatedTeams });
}

// --- Thresholds ---

/**
 * Update OE warning thresholds
 */
export async function updateThresholds(orgId, thresholds) {
  guardWrite('threshold_update', { summary: 'changed the OE warning thresholds', organizationId: orgId });
  await updateDoc(doc(db, COLLECTION_NAME, orgId), { thresholds });
}

/**
 * Learning Library preference (Aug 25): include or exclude the industry library for this organization.
 */
export async function updateLibraryPrefs(orgId, library) {
  guardWrite('library_prefs_update', { summary: 'changed the Learning Library preference', organizationId: orgId });
  await updateDoc(doc(db, COLLECTION_NAME, orgId), { library });
  invalidateLibraryPrefCache(orgId);
}

// --- Admins ---

/**
 * Add an admin UID to the organization
 */
export async function addAdmin(orgId, uid) {
  const org = await getOrganization(orgId);
  if (!org) throw new Error('Organization not found');

  if (!org.admins.includes(uid)) {
    const updatedAdmins = [...org.admins, uid];
    await updateDoc(doc(db, COLLECTION_NAME, orgId), { admins: updatedAdmins });
  }
}

/**
 * Remove an admin UID from the organization
 */
export async function removeAdmin(orgId, uid) {
  const org = await getOrganization(orgId);
  if (!org) throw new Error('Organization not found');

  const updatedAdmins = org.admins.filter((a) => a !== uid);
  if (updatedAdmins.length === 0) {
    throw new Error('Cannot remove the last admin');
  }
  await updateDoc(doc(db, COLLECTION_NAME, orgId), { admins: updatedAdmins });
}

export { DEFAULT_THRESHOLDS, generateId };
